import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

// ── Response schema ────────────────────────────────────────────────────────────

const fieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum([
    'string',
    'integer',
    'long',
    'decimal',
    'double',
    'date',
    'percent',
  ]),
  order: z.array(z.string()).optional(),
  tooltip: z.string().optional(),
});

const datasetCellSchema = z.union([z.string(), z.number(), z.null()]);
const datasetRowSchema = z.array(datasetCellSchema);

const metricsDatasetV2Schema = z.object({
  label: z.string(),
  description: z.string().nullish(),
  fields: z.array(fieldSchema),
  data: z.array(datasetRowSchema),
});

export type MetricsDatasetV2Response = z.infer<typeof metricsDatasetV2Schema>;
export type MetricsDatasetField = z.infer<typeof fieldSchema>;
export type MetricsDatasetRow = z.infer<typeof datasetRowSchema>;

// ── Request body ───────────────────────────────────────────────────────────────

export interface MetricsDatasetV2Request {
  orgId: string;
  metricType: string;
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  edApps: string[];
  classIds?: string[] | null;
  studentIds?: string[] | null;
}

// ── Hook props ─────────────────────────────────────────────────────────────────

export interface UseMetricsDatasetV2ApiProps extends MetricsDatasetV2Request {
  enabled?: boolean;
}

// ── Fetcher ────────────────────────────────────────────────────────────────────

const getMetricsDatasetV2Api = async ({
  orgId,
  metricType,
  teacherIds,
  startDate,
  endDate,
  edApps,
  classIds,
  studentIds,
}: MetricsDatasetV2Request) =>
  fetcher(`/metrics-dataset/${metricType}`, {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify({
      orgId,
      metricType,
      teacherIds,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate, { boundary: 'end' }),
      edApps,
      classIds: classIds ?? null,
      studentIds: studentIds ?? null,
    }),
  }).then((res) => metricsDatasetV2Schema.parse(res));

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useMetricsDatasetV2Api({
  enabled,
  ...request
}: UseMetricsDatasetV2ApiProps) {
  const {
    metricType,
    orgId,
    teacherIds,
    startDate,
    endDate,
    edApps,
    classIds,
    studentIds,
  } = request;

  const query = useQuery({
    queryKey: [
      'metrics-dataset-v2',
      {
        metricType,
        orgId,
        teacherIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
        classIds: classIds ?? null,
        studentIds: studentIds ?? null,
      },
    ],
    queryFn: () => getMetricsDatasetV2Api(request),
    enabled: enabled !== false,
    staleTime: 3 * 60 * 1000, // 3 Min Stale Time
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    loading: query.isLoading,
    error: query.error,
  };
}
